/**
 * Glyph — one icon element over Bower's two icon libraries.
 *
 * WHY. "Which library holds this glyph?" is a fact the registries already
 * carry, and before this component every consumer of a registry answered it
 * again in JSX: a ternary with a `<BowerIcon>` on one arm and a `<MaterialIcon>`
 * on the other, each arm spelling out its own size and its own gradient wiring.
 * Six sites did it, the two arms are styled through completely different
 * mechanisms (see `theme/iconGradient.ts`), and the predictable thing happened
 * — `SearchableMultiSelectMenu` painted its Bower arm in the ramp and its
 * Material arm flat, so whether a filter chip was branded came down to which
 * library its glyph happened to live in.
 *
 * WHAT IT DELIBERATELY DOES NOT DO. It does not try to be a union of the two
 * components' prop surfaces. `MaterialIcon` has `weight` / `filled` / `grade`
 * and a token-scale `boxSize`; `BowerIcon` has three fixed pixel-grid assets
 * and a CSS-length `size`. A prop that exists on one arm and silently does
 * nothing on the other is worse than two components. So this carries only what
 * BOTH libraries can honestly answer:
 *
 *   name      the glyph
 *   lib       which library holds it
 *   size      a CSS length, applied to both (MaterialIcon resolves a scalar
 *             `boxSize` to the glyph's font size, so "16px" means 16px on
 *             either arm)
 *   gradient  the brand ramp, by whichever mechanism that arm needs
 *
 * Anything beyond that — a weight, a fill, an accessible name — is a reason to
 * reach for the underlying component directly rather than to widen this one.
 *
 * DECORATIVE BY CONSTRUCTION. Both arms default to `aria-hidden`, and this
 * component exposes no way to change that. Every current consumer renders the
 * glyph beside a visible label that carries the meaning; a site where the icon
 * IS the meaning should name the containing control instead.
 */

import { BowerIcon, type BowerIconName } from './BowerIcon';
import { MaterialIcon } from './MaterialIcon';
import type { IconGradientVariant } from '../../theme/iconGradient';
import type { GlyphLib } from '../../theme/glyph';

export type { GlyphLib };

interface GlyphProps {
  /** Glyph name, resolved against `lib`. */
  readonly name: string;
  /** Defaults to Material Symbols — the broad set. */
  readonly lib?: GlyphLib;
  /** CSS length, e.g. "16px". Applies to both libraries. */
  readonly size?: string;
  /**
   * Paint in the brand ramp. Identity icons only — `theme/iconGradient.ts`
   * carries the rule. Absent means flat, on both arms.
   */
  readonly gradient?: IconGradientVariant;
  /**
   * Ink for a FLAT glyph. Ignored when `gradient` is set, which paints through
   * `color: transparent` on both arms.
   *
   * Defaults to `inherit` so a parent can colour a group — a selected tab, a
   * per-type accent, a muted empty state. That default is the one thing here
   * that MUST be stated rather than left to the arms: `BowerIcon` already
   * defaults to `currentColor`, but `MaterialIcon` defaults to a fixed
   * `text.secondary`, so leaving it unset made the same `GlyphRef` inherit or
   * not depending on which library held it — the exact split this component
   * exists to remove, and it silently unhooked three call sites from their
   * parent's colour when they migrated.
   */
  readonly color?: string;
}

export function Glyph({
  name,
  lib = 'material',
  size = '16px',
  gradient,
  color = 'inherit',
}: GlyphProps) {
  if (lib === 'bower') {
    // `name` is `string` because every registry that feeds this holds both
    // libraries' names in one field. The cast is checked at runtime by
    // BowerIcon, which throws naming the glyph — deliberately there rather
    // than here, so the check lives with the registry it is about and this
    // component needs nothing from BowerIcon but the component itself.
    return <BowerIcon name={name as BowerIconName} size={size} gradient={gradient} color={color} />;
  }
  return <MaterialIcon name={name} boxSize={size} gradient={gradient} color={color} />;
}
